














































































































































































































import Vue from "vue"
import { Component } from "vue-property-decorator"
import NiPage from "@nielsen-media/nlsn-vue-ui/lib/components/NiPage.vue"
import NiTableSearch from "@nielsen-media/nlsn-vue-ui/lib/components/NiTableSearch.vue"
import { sanatize } from "@/helpers/filter_chips.helper"
import { emitAlert } from "@/helpers/alert.helper"
import { EVENTS } from "@/constants"
import { CatOrganization } from "@/store/modules/test-sessions/test-sessions"
import { AdminUser } from "@/store/modules/admin-users/admin-users"

@Component({
  components: { NiPage, NiTableSearch },
})
export default class AdminUsersView extends Vue {
  title = "Users"
  showFilterSheet = false
  showAddUserDialog = false
  addUserForm = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    catOrganizationId: "",
  }
  addUserFormValid = false
  addUserShowPassword = false
  addUserSaving = false
  showUpdateOrgDialog = false
  userForUpdateOrg: AdminUser | null = null
  updateOrgCatOrganizationId = ""
  updateOrgSaving = false

  addUserFormDefault() {
    return {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      catOrganizationId: "",
    }
  }

  get addUserEmailRules(): ((v: string) => boolean | string)[] {
    return [
      (v) => !!v || "Email is required",
      (v) => /.+@.+\..+/.test(v) || "Valid email required",
      (v) =>
        !this.isNielsenEmail(v) ||
        "Users cannot be added with a @nielsen.com email address.",
    ]
  }

  get isAddUserNielsenEmail(): boolean {
    return this.isNielsenEmail(this.addUserForm.email)
  }

  isNielsenEmail(email: string): boolean {
    const trimmed = (email || "").trim().toLowerCase()
    return trimmed.length > 0 && trimmed.endsWith("@nielsen.com")
  }

  get addUserPasswordRules(): ((v: string) => boolean | string)[] {
    return [
      (v) => !!v || "Password is required",
      (v) => !v || v.length >= 8 || "At least 8 characters required",
      (v) => !v || /[a-z]/.test(v) || "At least one lowercase letter required",
      (v) => !v || /[A-Z]/.test(v) || "At least one uppercase letter required",
      (v) => !v || /[0-9]/.test(v) || "At least one number required",
      (v) => !v || /[^A-Za-z0-9]/.test(v) || "At least one special character required",
    ]
  }

  headers = [
    { text: "Name", value: "name", align: "left", sortable: false },
    { text: "Email", value: "email", align: "left", sortable: false },
    { text: "CAT Organization", value: "catOrganizationName", align: "left", sortable: false },
    { text: "Update organization", value: "actions", align: "left", sortable: false },
  ]

  created(): void {
    this.$store.dispatch("testSessions/loadCatOrganizations")
    this.$store.dispatch("adminUsers/fetchUsers")
  }

  get users(): AdminUser[] {
    return this.$store.getters["adminUsers/users"]
  }

  get loading(): boolean {
    return this.$store.getters["adminUsers/loading"]
  }

  get userSearch(): string {
    return this.$store.getters["adminUsers/userSearch"]
  }

  get organizationSearch(): string {
    return this.$store.getters["adminUsers/organizationSearch"]
  }

  get page(): number {
    return this.$store.getters["adminUsers/page"]
  }

  get itemsPerPage(): number {
    return this.$store.getters["adminUsers/itemsPerPage"]
  }

  get totalCount(): number {
    const count = this.$store.getters["adminUsers/totalCount"]
    return count > 0 ? count : 0
  }

  get catOrganizations(): CatOrganization[] {
    return this.$store.getters["testSessions/catOrganizations"]
  }

  get filterChips(): string[] {
    const filters = {
      catOrganizationId: this.organizationSearch || undefined,
    }
    return sanatize(filters, [], this.catOrganizations)
  }

  get paginatedUsers(): AdminUser[] {
    const start = (this.page - 1) * this.itemsPerPage
    return this.users.slice(start, start + this.itemsPerPage)
  }

  orgNameById(id: string | null): string | undefined {
    if (!id) return undefined
    const org = this.catOrganizations.find((o) => o.id === id)
    return org ? org.name : undefined
  }

  onUserSearchInput(value: string): void {
    this.$store.dispatch("adminUsers/updateUserSearch", value || "")
  }

  updateCatOrganization(organizationId: string): void {
    this.$store.dispatch("adminUsers/updateOrganizationSearch", organizationId || "")
  }

  clearCatOrganization(): void {
    this.$store.dispatch("adminUsers/updateOrganizationSearch", "")
  }

  clearFilter(): void {
    this.$store.dispatch("adminUsers/updateOrganizationSearch", "")
  }

  updatePage(pageNumber: number): void {
    if (this.loading) return
    if (this.page === pageNumber) return
    this.$store.dispatch("adminUsers/setPage", pageNumber)
  }

  updateItemsPerPage(val: number): void {
    if (this.itemsPerPage === val) return
    this.$store.dispatch("adminUsers/setItemsPerPage", val)
  }

  onAddUserDialogChange(open: boolean): void {
    if (!open) this.resetAddUserForm()
  }

  onAddUserCancel(): void {
    this.showAddUserDialog = false
    this.resetAddUserForm()
  }

  resetAddUserForm(): void {
    this.addUserForm = { ...this.addUserFormDefault() }
    const form = this.$refs.addUserForm as { resetValidation?: () => void }
    if (form?.resetValidation) form.resetValidation()
  }

  async onAddUserSave(): Promise<void> {
    if (this.isNielsenEmail(this.addUserForm.email)) return
    const form = this.$refs.addUserForm as { validate: () => Promise<boolean> } | undefined
    const valid = form ? await form.validate() : false
    if (!valid || !this.addUserForm.catOrganizationId) return
    this.addUserSaving = true
    try {
      await this.$store.dispatch("adminUsers/createClientAdminUser", {
        firstname: this.addUserForm.firstname.trim(),
        lastname: this.addUserForm.lastname.trim(),
        email: this.addUserForm.email.trim(),
        password: this.addUserForm.password,
        catOrganizationId: this.addUserForm.catOrganizationId,
      })
      this.showAddUserDialog = false
      this.resetAddUserForm()
    } finally {
      this.addUserSaving = false
    }
  }

  openUpdateOrgDialog(user: AdminUser): void {
    this.userForUpdateOrg = user
    this.updateOrgCatOrganizationId = user.catOrganizationId || ""
    this.showUpdateOrgDialog = true
  }

  async onUpdateOrgSave(): Promise<void> {
    if (!this.userForUpdateOrg || !this.updateOrgCatOrganizationId) return
    if (this.updateOrgCatOrganizationId === (this.userForUpdateOrg.catOrganizationId || "")) {
      emitAlert(EVENTS.GLOBAL_WARNING, "Selected organization is the same as the current one. Please choose a different organization.")
      return
    }
    this.updateOrgSaving = true
    try {
      await this.$store.dispatch("adminUsers/updateUserOrganization", {
        userId: this.userForUpdateOrg.id,
        catOrganizationId: this.updateOrgCatOrganizationId,
      })
      this.showUpdateOrgDialog = false
      this.userForUpdateOrg = null
      this.updateOrgCatOrganizationId = ""
    } finally {
      this.updateOrgSaving = false
    }
  }
}
